import type User from 'benchmarking-domain/user';
import { firebaseApp } from 'firebase-app';
import {
  browserSessionPersistence,
  connectAuthEmulator,
  signOut as firebaseSignOut,
  User as FirebaseUser,
  getAuth,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithEmailLink,
} from 'firebase/auth';
import { enqueueSnackbar } from 'notistack';

export interface SignUpParams {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface SignInWithOAuthParams {
  provider: 'google' | 'discord';
}

export interface SignInWithPasswordParams {
  email: string;
  password: string;
}

export interface SignInWithLinkToEmailParams {
  email: string;
}

export interface ResetPasswordParams {
  email: string;
}

const auth = getAuth(firebaseApp);
setPersistence(auth, browserSessionPersistence);

if (location.hostname === 'localhost') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099');
}

class AuthClient {
  async signUp(_: SignUpParams): Promise<{ error?: string }> {
    return { error: 'Sign up not implemented' };
  }

  async signInWithOAuth(_: SignInWithOAuthParams): Promise<{ error?: string }> {
    return { error: 'Social authentication not implemented' };
  }

  async signInWithPassword(params: SignInWithPasswordParams): Promise<{ error?: string }> {
    const { email, password } = params;

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      return { error: 'Invalid credentials' };
    }

    return {};
  }

  async signInWithEmailLink(params: SignInWithLinkToEmailParams): Promise<{ error?: string }> {
    try {
      await signInWithEmailLink(auth, params.email, window.location.href);
      return {};
    } catch (error) {
      let errorMessage: string;

      if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = String(error);
      }
      this.handleAuthError(error);

      return { error: errorMessage };
    }
  }

  async signInWithLinkToEmail(params: SignInWithLinkToEmailParams): Promise<{ error?: string }> {
    const actionCodeSettings = {
      url: window.location.href,
      // This must be true.
      handleCodeInApp: true,
    };

    return sendSignInLinkToEmail(auth, params.email, actionCodeSettings)
      .then(() => {
        window.localStorage.setItem('emailForSignIn', params.email);
        return {};
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        // ...
        return { error: errorMessage };
      });
  }

  async resetPassword(_: ResetPasswordParams): Promise<{ error?: string }> {
    return { error: 'Password reset not implemented' };
  }

  async updatePassword(_: ResetPasswordParams): Promise<{ error?: string }> {
    return { error: 'Update reset not implemented' };
  }

  async getUser(): Promise<{ data?: User | null; pendingEmailConfirmation: boolean; error?: string }> {
    return auth.authStateReady().then(() => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return this.checkIfLinkSignIn().then((checkResult) => {
          if (checkResult.pendingEmailConfirmation) {
            return { data: null, pendingEmailConfirmation: true, error: checkResult.error };
          } else if (checkResult.user) {
            return this.fireBaseUserToUser(checkResult.user).then((user) => {
              return { data: user, pendingEmailConfirmation: false, error: checkResult.error };
            });
          }
          return { pendingEmailConfirmation: false, error: checkResult.error };
        });
      }

      return this.fireBaseUserToUser(currentUser).then((user) => {
        return { data: user, pendingEmailConfirmation: false };
      });
    });
  }

  private async checkIfLinkSignIn(): Promise<{
    user?: FirebaseUser;
    pendingEmailConfirmation: boolean;
    error?: string;
  }> {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        return { pendingEmailConfirmation: true };
      }

      return await signInWithEmailLink(auth, email, window.location.href)
        .then((result) => {
          window.localStorage.removeItem('emailForSignIn');

          return { user: result.user as FirebaseUser, pendingEmailConfirmation: false };
        })
        .catch((error) => {
          this.handleAuthError(error);

          return { pendingEmailConfirmation: false, error: error?.message as string };
        });
    }

    return { pendingEmailConfirmation: false };
  }

  private handleAuthError(error: any): void {
    if (error?.message.includes('auth/user-disabled') || error?.message.includes('PERMISSION_DENIED')) {
      enqueueSnackbar('You are not authorized to sign up. Please contact support for assistance.', {
        variant: 'warning',
        autoHideDuration: 10 * 1000,
      });
    }

    if (error?.message.includes('auth/invalid-action-code')) {
      this.clearOobCode();
    }
  }

  private clearOobCode(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.has('oobCode')) {
      url.searchParams.delete('oobCode');
      window.history.pushState({}, '', url);
    }
  }

  private async fireBaseUserToUser(firebaseUser: FirebaseUser) {
    let idTokenResult = await firebaseUser.getIdTokenResult(true);
    return {
      id: firebaseUser.uid,
      name: firebaseUser.displayName ?? undefined,
      email: firebaseUser.email!,
      organisationIds: idTokenResult.claims['organisationIds'] as string[],
      organisations: idTokenResult.claims['organisations'] as string[],
      roles: idTokenResult.claims['roles'] as string[],
    };
  }

  async signOut(): Promise<{ error?: string }> {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      return { error: 'Sign out failed' };
    }

    return {};
  }
}

export const authClient = new AuthClient();
